'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { Partner, PartnerStatus, STATUSES, CATEGORIES, COUNTRIES } from '@/types';
import PartnerCard from './PartnerCard';
import PartnerDetailModal from './PartnerDetailModal';

export default function PipelineBoard() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Partner | null>(null);

  // filtros
  const [fCategory, setFCategory] = useState('all');
  const [fCountry, setFCountry] = useState('all');
  const [fPriority, setFPriority] = useState('all');
  const [fType, setFType] = useState('all');
  const [query, setQuery] = useState('');

  const fetchPartners = useCallback(async () => {
    try {
      const res = await fetch('/api/partners');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPartners(data as Partner[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return partners.filter((p) => {
      if (fCategory !== 'all' && p.category !== fCategory) return false;
      if (fCountry !== 'all' && p.country !== fCountry) return false;
      if (fPriority !== 'all' && p.priority !== fPriority) return false;
      if (fType !== 'all' && p.partnership_type !== fType) return false;
      if (q) {
        const hay = [p.partner, p.category, p.country, p.notes, p.intro_path].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [partners, fCategory, fCountry, fPriority, fType, query]);

  const columns = useMemo(() => {
    const cols: Record<PartnerStatus, Partner[]> = {
      not_started: [], researching: [], intro_sent: [], meeting_set: [],
      negotiating: [], signed: [], stalled: [], passed: [],
    };
    for (const p of filtered) cols[p.status]?.push(p);
    return cols;
  }, [filtered]);

  const updatePartner = useCallback(async (id: string, patch: Partial<Partner>) => {
    // optimista
    setPartners((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    try {
      const res = await fetch(`/api/partners/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      fetchPartners(); // revertir desde la fuente
    }
  }, [fetchPartners]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    const newStatus = destination.droppableId as PartnerStatus;
    updatePartner(draggableId, { status: newStatus });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Cargando pipeline…</div>;
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-600 gap-2">
        <p>{error}</p>
        <button onClick={() => { setError(''); setLoading(true); fetchPartners(); }} className="px-3 py-1.5 bg-red-100 rounded-lg text-sm">Reintentar</button>
      </div>
    );
  }

  const selectClass = 'text-xs sm:text-sm px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-gray-300 cursor-pointer';

  return (
    <div className="h-full flex flex-col">
      {/* Filtros */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar partner…"
          className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white w-44 focus:outline-none focus:border-[#0890F1]"
        />
        <select value={fCountry} onChange={(e) => setFCountry(e.target.value)} className={selectClass}>
          <option value="all">🌎 Todos los países</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={fCategory} onChange={(e) => setFCategory(e.target.value)} className={selectClass}>
          <option value="all">Todas las categorías</option>
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
        </select>
        <select value={fPriority} onChange={(e) => setFPriority(e.target.value)} className={selectClass}>
          <option value="all">Toda prioridad</option>
          <option value="high">Alta</option>
          <option value="medium">Media</option>
          <option value="low">Baja</option>
        </select>
        <select value={fType} onChange={(e) => setFType(e.target.value)} className={selectClass}>
          <option value="all">Estratégico + local</option>
          <option value="strategic_global">Estratégico</option>
          <option value="local_operational">Local</option>
        </select>
        <span className="text-sm text-gray-400 ml-auto">{filtered.length} partners</span>
      </div>

      {/* Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4 flex-1">
          {STATUSES.map((s) => (
            <div key={s.id} className="flex-shrink-0 w-64">
              <div className="flex items-center gap-2 mb-2 px-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                <span className="text-sm font-semibold text-gray-700">{s.label}</span>
                <span className="text-xs text-gray-400">{columns[s.id].length}</span>
              </div>
              <Droppable droppableId={s.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[120px] rounded-xl p-2 transition-colors ${
                      snapshot.isDraggingOver ? 'bg-[#0890F1]/5' : 'bg-gray-100/60'
                    }`}
                  >
                    {columns[s.id].map((p, i) => (
                      <PartnerCard key={p.id} partner={p} index={i} onClick={() => setSelected(p)} />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>

      {selected && (
        <PartnerDetailModal
          partner={partners.find((p) => p.id === selected.id) || selected}
          onClose={() => setSelected(null)}
          onSave={updatePartner}
        />
      )}
    </div>
  );
}
